
import { useState } from 'react';

const FinalCTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    challenge: '',
    package: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Convert the plain object into URL‑encoded form data
      const urlEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        urlEncoded.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d46de4ihchb23d4ef8ig', {
        method: 'POST',
        headers: {
          // Explicitly tell the server we are sending URL‑encoded data
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: urlEncoded,
      });

      if (!response.ok) {
        // Capture non‑2xx responses as errors
        const errorText = await response.text();
        console.error('Form submission failed:', response.status, errorText);
        throw new Error(`Server responded with ${response.status}`);
      }

      // Success path
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        business: '',
        challenge: '',
        package: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="final-cta" className="py-20 bg-gradient-to-br from-[#394851] to-[#748881] text-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h3 className="text-4xl font-bold mb-6">
            Your business is too good to stay invisible. Let's change that.
          </h3>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-4">
            First call is free. No pitch. Just honesty.
          </p>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            You've tried everything else. What if the answer isn't more effort—it's the right words?
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit} data-readdy-form id="strategy-call-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#394851] mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#748881] focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#394851] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#748881] focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#394851] mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#748881] focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="business" className="block text-sm font-semibold text-[#394851] mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  id="business"
                  name="business"
                  value={formData.business}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#748881] focus:border-transparent text-sm"
                  placeholder="Your business name"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="package" className="block text-sm font-semibold text-[#394851] mb-2">
                What sounds right for where you are?
              </label>
              <div className="relative">
                <select
                  id="package"
                  name="package"
                  value={formData.package}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#748881] focus:border-transparent text-sm appearance-none bg-white"
                >
                  <option value="">I'm not sure yet</option>
                  <option value="takeoff">Takeoff - $750 (Get the foundation right)</option>
                  <option value="flight-path">Flight Path - $1,850 (Complete transformation)</option>
                  <option value="first-class">First Class - $3,500+ (Full partnership)</option>
                  <option value="just-exploring">Just exploring options</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="challenge"
                className="block text-sm font-semibold text-[#394851] mb-2"
              >
                What's keeping you up at night about your business? *
              </label>
              <textarea
                id="challenge"
                name="challenge"
                value={formData.challenge}
                onChange={handleInputChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#748881] focus:border-transparent text-sm resize-none"
                placeholder="Tell me what's really going on. The marketing that's not working, the customers who aren't finding you, the money you're losing... I've heard it all."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">
                {formData.challenge.length}/500 characters
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-[#A8AE9C]/20 border border-[#A8AE9C]/50 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-[#394851] mr-2"></i>
                  <p className="text-[#394851] font-medium">
                    Got it. I'll reach out within 24 hours to schedule our conversation.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2"></i>
                  <p className="text-red-800 font-medium">
                    Something went wrong. Try again or just call me directly.
                  </p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || formData.challenge.length > 500}
              className="w-full bg-[#394851] text-white py-4 rounded-lg text-lg font-semibold hover:bg-[#748881] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Sending...' : 'Let\'s Talk About Your Legacy'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-[#748881] mb-4">
              <strong>What happens next:</strong>
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-[#748881]">
              <div className="flex items-center justify-center">
                <i className="ri-time-line text-[#394851] mr-2"></i>
                <span>30 minutes of honest conversation</span>
              </div>
              <div className="flex items-center justify-center">
                <i className="ri-shield-check-line text-[#394851] mr-2"></i>
                <span>Zero sales pressure</span>
              </div>
              <div className="flex items-center justify-center">
                <i className="ri-lightbulb-line text-[#394851] mr-2"></i>
                <span>Real insights you can use</span>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-[#394851]/5 rounded-lg">
              <p className="text-[#748881] italic">
                "This isn't about selling you something. 
This is about understanding if we're the right fit to help build your legacy."
              </p>
              <p className="text-[#394851] font-medium mt-2">
                — Sebastian
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
